import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../firebase";
import api from "../services/api";

export default function PricingPage() {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const navigate = useNavigate();
  const [user] = useAuthState(auth);

  const handleUpgrade = () => {
    if (!user) {
      navigate("/signup?next=/dashboard&plan=pro&source=pricing");
      return;
    }

    setLoading(true);
    api
      .post("/stripe/create-checkout-session", {
        uid: user.uid,
        email: user.email,
        source: "pricing",
        planType: "pro",
      })
      .then((res) => {
        window.location.href = res.data.url;
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: "Upgrade failed. Please try again.",
          severity: "error",
        });
        setLoading(false);
      });
  };

  return (
    <>
      <Navbar />
      <Box sx={{ mt: "80px", py: 10, backgroundColor: "#f9fafb" }}>
        <Container maxWidth="md">
          <Typography variant="h4" align="center" fontWeight={700} gutterBottom>
            Start free – upgrade only when you're ready
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            14-day free trial · No credit card required · Cancel anytime
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {/* Free Plan */}
            <Grid xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 3,
                  textAlign: "center",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                  transition: "transform 0.2s",
                  "&:hover": { transform: "scale(1.015)" },
                }}
              >
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Free Trial
                </Typography>
                <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
                  €0
                </Typography>

                <Box sx={{ textAlign: "left", mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✓ Unlimited uploads for 14 days
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✓ Access to all features
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✓ Invoicing & payroll included
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✓ Smart inbox & filters
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✓ Track profit & expenses by project
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✓ Manual reports & ZIP export
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    sx={{ mt: 2 }}
                  >
                    — After 14 days, upgrade to continue
                  </Typography>
                </Box>

                <Button
                  component={RouterLink}
                  to="/signup"
                  variant="outlined"
                  fullWidth
                  sx={{ borderRadius: "30px", fontWeight: 600 }}
                >
                  Start for Free
                </Button>
              </Paper>
            </Grid>

            {/* Pro Plan */}
            <Grid xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 3,
                  textAlign: "center",
                  border: "2px solid #3b82f6",
                  backgroundColor: "#fff",
                  boxShadow: "0 2px 10px rgba(30,64,175,0.08)",
                  transition: "transform 0.2s",
                  "&:hover": { transform: "scale(1.015)" },
                  position: "relative",
                }}
              >
                <Chip
                  label="Most Popular"
                  color="primary"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    fontWeight: 600,
                  }}
                />
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  color="primary"
                >
                  Pro
                </Typography>
                <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
                  €29.99/month
                </Typography>

                <Box sx={{ textAlign: "left", mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✓ Unlimited uploads
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✓ Auto-send reports to your accountant
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✓ Invoicing & payroll tracking
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✓ PDF receipt support
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✓ VAT breakdown & summaries
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✓ ZIP export for accountants
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✓ Deadline reminders & alerts
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✓ Full project profit & expense tracking with detailed
                    reports
                  </Typography>

                  <Typography variant="body2">
                    ✓ Priority email support
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  onClick={handleUpgrade}
                  sx={{
                    borderRadius: "30px",
                    fontWeight: 600,
                    minHeight: 48,
                    backgroundColor: "#3b82f6",
                    "&:hover": { backgroundColor: "#2563eb" },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Upgrade Now"
                  )}
                </Button>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, display: "block" }}
                >
                  Cancel anytime · Billed in EUR
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 10, textAlign: "center" }}>
            <Typography variant="h6" gutterBottom>
              Save time. Stay compliant. Ditch spreadsheets.
            </Typography>
            <Button
              variant="contained"
              component={RouterLink}
              to="/signup"
              size="large"
              sx={{
                backgroundColor: "#22c55e",
                fontWeight: 600,
                px: 4,
                py: 1.2,
                mt: 1,
                "&:hover": { backgroundColor: "#16a34a" },
              }}
            >
              Try SortlyBooks Free
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              No credit card required · 14-day free trial · Cancel anytime
            </Typography>
          </Box>
        </Container>
      </Box>

      <Box sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">
          All plans include secure backups & GDPR-compliant storage.
        </Typography>
        <Typography variant="caption">
          PDF receipt support included in Pro.
        </Typography>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
